import React from "react";
import { Button, Input, Typography } from "antd";
import { AudioOutlined, LoadingOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Error as ErrorMessage } from "../../../commons/Messages/Messages";
import useDictation from "../measurements/useDictation";
import { transcribeAudio } from "./api";

/** Appends spoken text to typed text with a sensible separator. */
export const appendText = (current, addition) => {
  if (!addition) return current || "";
  const text = current || "";
  const glue = !text ? "" : /\n$/.test(text) ? "" : /[.!?]$/.test(text.trim()) ? " " : ". ";
  return `${text}${glue}${addition}`;
};

/**
 * A text section of the day, typed or spoken (live recognition in the browser, else the
 * recording is written on the server; the "Translate voice input into" preference applies).
 */
const DictationField = ({
  value,
  onChange,
  placeholder,
  voiceConfigured,
  voiceTranslateTo,
  disabled,
  minRows = 3
}) => {
  const { t, i18n } = useTranslation("dashboard");
  const dictation = useDictation({
    language: i18n.language,
    serverAvailable: voiceConfigured,
    translate: Boolean(voiceTranslateTo),
    transcribe: transcribeAudio,
    onText: spoken => onChange(appendText(value, spoken)),
    onError: (code, err) => {
      if (code === "server") return generalErrorAlert(err);
      const messages = {
        microphone: "Please allow access to the microphone.",
        empty: "Nothing was understood. Please try again.",
        unsupported: "Voice input is not available on this device.",
        live: "Speech recognition stopped. Please try again."
      };
      ErrorMessage(t(messages[code] || messages.live));
    }
  });
  const active = dictation.status === "listening" || dictation.status === "recording";
  const busy = dictation.status === "processing" || dictation.status === "starting";

  return (
    <div className="sdi-dictation">
      <Input.TextArea
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        autoSize={{ minRows, maxRows: 14 }}
        maxLength={20000}
        placeholder={placeholder}
      />
      {active && (
        <Typography.Paragraph type="secondary" className="sdi-interim">
          <span className="sdi-rec-dot" />{" "}
          {dictation.status === "listening"
            ? dictation.interim || t("Listening…")
            : t("The text appears when you stop.")}
        </Typography.Paragraph>
      )}
      {!disabled && dictation.available && (
        <Button
          className={active ? "sdi-mic sdi-mic--active" : "sdi-mic"}
          shape="round"
          type={active ? "primary" : "default"}
          danger={active}
          icon={busy ? <LoadingOutlined /> : active ? <StopOutlined /> : <AudioOutlined />}
          disabled={busy}
          onClick={active ? dictation.stop : dictation.start}
        >
          {dictation.status === "processing" ? t("Writing…") : active ? t("Stop") : t("Dictate")}
        </Button>
      )}
    </div>
  );
};

export default DictationField;
